<template>
  <div class="job-manager-dashboard">

<!--    <pre v-text="stats"></pre>-->

    <v-row class="mb-4">
      <v-col cols="12">
        <v-row no-gutters class="filter-bar" v-if="hasAnyFilterOptions">
          <v-col cols="12" class="d-flex align-center flex-wrap pa-3" style="gap: 12px;">
            <span class="text-body-2">{{ $t('filters') }} :</span>

            <v-select
                v-if="userOptions.length > 0"
                v-model="filters.user"
                :label="$t('job-manager.user')"
                :items="userOptions"
                dense
                outlined
                hide-details
                clearable
                class="filter-select"
                style="min-width: 150px; max-width: 200px;"
            />

            <v-select
                v-if="jobDefinitionOptions.length > 0"
                v-model="filters.jobDefinition"
                :label="$t('job-manager.definition')"
                :items="jobDefinitionOptions"
                dense
                outlined
                hide-details
                clearable
                class="filter-select"
                style="min-width: 150px; max-width: 200px;"
            />

            <v-select
                v-if="typeOptions.length > 0"
                v-model="filters.type"
                :label="$t('job-manager.type')"
                :items="typeOptions"
                dense
                outlined
                hide-details
                clearable
                class="filter-select"
                style="min-width: 120px; max-width: 150px;"
            />

            <v-select
                v-if="queueOptions.length > 0"
                v-model="filters.queue"
                :label="$t('job-manager.queue')"
                :items="queueOptions"
                dense
                outlined
                hide-details
                clearable
                class="filter-select"
                style="min-width: 150px; max-width: 200px;"
            />

            <v-select
                v-if="statusOptions.length > 0"
                v-model="filters.status"
                :label="$t('job-manager.status')"
                :items="statusOptions"
                item-text="text"
                item-value="value"
                dense
                outlined
                hide-details
                clearable
                class="filter-select"
                style="min-width: 120px; max-width: 150px;"
            />


            <v-btn
                text
                color="grey darken-1"
                @click="resetFilters"
                class="reset-btn flex-shrink-0"
                small
                v-if="hasActiveFilters"
            >
              {{ $t('job-manager.reset-filters') }}
            </v-btn>
          </v-col>
        </v-row>

        <!-- Active Filter Chips -->
        <div v-if="hasActiveFilters" class="filter-chips mt-3 d-flex flex-wrap">
          <v-chip
              v-if="filters.user"
              small
              close
              color="primary"
              text-color="white"
              class="mr-2 mb-2"
              @click:close="clearFilter('user')"
          >
            {{ $t('job-manager.user') }}: {{ filters.user }}
          </v-chip>
          <v-chip
              v-if="filters.jobDefinition"
              small
              close
              color="primary"
              text-color="white"
              class="mr-2 mb-2"
              @click:close="clearFilter('jobDefinition')"
          >
            {{ $t('job-manager.definition') }}: {{ filters.jobDefinition }}
          </v-chip>
          <v-chip
              v-if="filters.type"
              small
              close
              color="primary"
              text-color="white"
              class="mr-2 mb-2"
              @click:close="clearFilter('type')"
          >
            {{ $t('job-manager.type') }}: {{ filters.type }}
          </v-chip>
          <v-chip
              v-if="filters.queue"
              small
              close
              color="primary"
              text-color="white"
              class="mr-2 mb-2"
              @click:close="clearFilter('queue')"
          >
            {{ $t('job-manager.queue') }}: {{ filters.queue }}
          </v-chip>
          <v-chip
              v-if="filters.status"
              small
              close
              color="primary"
              text-color="white"
              class="mr-2 mb-2"
              @click:close="clearFilter('status')"
          >
            {{ $t('job-manager.status') }}: {{ filters.status }}
          </v-chip>
        </div>
      </v-col>
    </v-row>


    <headline>
      {{ $t('job-manager.dashboard.live-activity') }} (LIVE)
    </headline>
    <!-- Section: Activité en cours (LIVE) - Always visible -->
    <section class="dashboard-section mb-6">
      <!-- Live KPIs (3 cards) -->


      <!-- Live Charts Row -->
      <v-row>
        <v-col cols="12" md="3">
          <kpi-card
              :label="$t('job-manager.dashboard.running')"
              :value="liveStats.running ?? 0"
              color="info"
              class="mb-4"
          />
        </v-col>
        <v-col cols="12" md="3">
          <kpi-card
              :label="$t('job-manager.dashboard.pending')"
              :value="stats?.live?.pending ?? 0"
              color="warning"
          />
        </v-col>
        <v-col cols="12" md="2">
          <kpi-card
              label="Workers"
              :value="Object.keys(stats?.workers ?? []).length"
              color="warning"
          />
        </v-col>
        <v-col cols="12" md="2">
          <kpi-card
              label="Queues"
              :value="Object.keys(stats?.queues ?? []).length"
              color="warning"
          />
        </v-col>
        <v-col cols="12" md="2">
          <kpi-card
              label="Servers"
              :value="Object.keys(stats?.machines ?? []).length"
              color="warning"
          />
        </v-col>

      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <live-stacked-bars
              :title="$t('job-manager.dashboard.by-definition')"
              :data="liveDefinitionStackedData"
              :max-items="10"
              @segment-click="onDefinitionStatusClick"
              style="height: 100%"
          />
        </v-col>
        <v-col cols="12" md="6">
          <live-stacked-bars
              :title="$t('job-manager.dashboard.by-queue')"
              :data="liveQueueStackedData"
              :max-items="10"
              @segment-click="onQueueStatusClick"
              style="height: 100%"
          />
        </v-col>

      </v-row>
    </section>

    <!-- Section: Dernière heure (Historique 1h) -->
    <section v-if="hasHistoricalData" class="dashboard-section mb-6">
      <v-row class="mb-3">
        <v-col cols="12">
          <h2 class="text-h6 mb-0">{{ $t('job-manager.dashboard.last-hour') }}</h2>
        </v-col>
      </v-row>


      <!-- Historical KPIs (6 cards) -->
      <v-row class="mb-4">
        <v-col cols="12" md="2">
          <kpi-card
              :label="$t('job-manager.dashboard.tasks-1h')"
              :value="historicalStats.tasks"
              suffix="jobs"
          />
        </v-col>
        <v-col cols="12" md="2">
          <kpi-card
              :label="$t('job-manager.dashboard.failures-1h')"
              :value="historicalStats.failures"
              suffix="jobs"
              color="error"
          />
        </v-col>
        <v-col cols="12" md="2">
          <kpi-card
              :label="$t('job-manager.dashboard.success-rate-1h')"
              :value="historicalStats.successRate"
              :decimals="1"
              suffix="%"
              color="success"
          />
        </v-col>
        <v-col cols="12" md="2">
          <kpi-card
              :label="$t('job-manager.dashboard.max-wait-1h')"
              :value="historicalStats.maxWait"
              :decimals="2"
              suffix="s"
          />
        </v-col>
        <v-col cols="12" md="2">
          <kpi-card
              :label="$t('job-manager.dashboard.avg-duration-1h')"
              :value="historicalStats.avgDuration"
              :decimals="2"
              suffix="s"
          />
        </v-col>
        <v-col cols="12" md="2">
          <kpi-card
              :label="$t('job-manager.dashboard.tps-1min')"
              :value="historicalStats.tps"
              :decimals="2"
              suffix="t/min"
              :sparkline="tpsSparklineData"
          />
        </v-col>
      </v-row>

      <!-- Historical Distributions (3 cards) -->
      <v-row>
        <v-col cols="12" md="4">
          <distribution-chart
              :title="$t('job-manager.dashboard.by-definition')"
              :data="definitionDistribution"
              :max-items="8"
              @item-click="onDefinitionClick"
          />
        </v-col>
        <v-col cols="12" md="4">
          <distribution-chart
              :title="$t('job-manager.dashboard.by-queue')"
              :data="queueDistribution"
              :max-items="8"
              @item-click="onQueueClick"
          />
        </v-col>
        <v-col cols="12" md="4">
          <distribution-chart
              :title="$t('job-manager.dashboard.by-status')"
              :data="statusDistribution"
              :max-items="8"
              @item-click="onStatusClick"
          />
        </v-col>
      </v-row>
    </section>

    <!-- Section: Activité récente -->
    <section v-if="hasRecentActivity" class="dashboard-section mb-6">
      <v-row class="mb-3">
        <v-col cols="12">
          <h2 class="text-h6 mb-3">{{ $t('job-manager.recent-activity') }}</h2>
          <v-text-field
              v-model="activitySearch"
              :placeholder="$t('job-manager.dashboard.recent-activity-search-placeholder')"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
              class="mb-4"
          />
        </v-col>
      </v-row>

      <recent-activity-table
          :items="filteredRecentActivity"
          :loading="loadingActivity"
          @row-click="showExecutionDetails"
      />
    </section>

    <!-- Section: Infrastructure -->
    <section class="dashboard-section infrastructure-section">
      <div class="d-flex align-center w-100 mb-3">
        <h2 class="text-h6 mb-0">{{ $t('job-manager.dashboard.infrastructure') }}</h2>
        <v-spacer />
        <v-chip x-small v-if="infraLiveError" color="warning" text-color="white">
          Flux interrompu, tentative de reconnexion…
        </v-chip>
      </div>
      <v-row class="infra-grid">
        <v-col cols="12">
          <workers-list-card :workers="workersList" />
        </v-col>
      </v-row>

      <v-row class="infra-grid">
        <v-col cols="12" md="6" class="infra-left">
          <queues-health-card :queues="queuesHealth" />
        </v-col>
        <v-col cols="12" md="6" class="infra-left">
          <machines-chips-card :machines="machinesData" />
        </v-col>
      </v-row>
    </section>

    <!-- Execution Details Side Panel -->
    <execution-details-panel
      v-model="showDetailsPanel"
      :execution="selectedExecution"
    />
  </div>
</template>

<script>
// Import components
import LiveStackedBars from '../elements/LiveStackedBars.vue';
import KpiCard from '../elements/KpiCard.vue';
import DistributionChart from '../elements/DistributionChart.vue';
import RecentActivityTable from '../elements/RecentActivityTable.vue';
import QueuesHealthCard from '../elements/QueuesHealthCard.vue';
import WorkersListCard from '../elements/WorkersListCard.vue';
import MachinesChipsCard from '../elements/MachinesChipsCard.vue';
import ExecutionDetailsPanel from '../elements/ExecutionDetailsPanel.vue';

import JobRepository from "@repository/JobRepository";
import {mapGetters} from "vuex";

export default {
  name: 'Dashboard',
  components: {
    LiveStackedBars,
    KpiCard,
    DistributionChart,
    RecentActivityTable,
    QueuesHealthCard,
    WorkersListCard,
    MachinesChipsCard,
    ExecutionDetailsPanel
  },
  data() {
    return {
      stats: null,
      infraLiveError: false,

      // Filter system
      filters: {
        user: null,
        jobDefinition: null,
        type: null,
        queue: null,
        status: null
      },


      // Activity search
      activitySearch: '',

      // Infrastructure expansion
      infrastructureExpanded: [0], // Open by default

      // Activity table
      loadingActivity: false,

      // Execution details panel
      showDetailsPanel: false,
      selectedExecution: null,

    };
  },

  mounted() {
    JobRepository.metrics().then(initialMetric => {
      // Handle initial metrics if needed
    });
    global.zmsSock.on("connect", () => {
      this.infraLiveError = false;
      this.onSocketConnection();
    });
    global.zmsSock.on("disconnect", () => {
      this.infraLiveError = true;
    });
    global.zmsSock.on("jobs:dashboard:update", (obj) => {
      this.stats = obj;
    });
    // Initial subscribe with current filters (normalized)
    this.setDashboardFilter(this.filters);
  },

  destroyed() {
    global.zmsSock.off("connect", this.onSocketConnection);
    global.zmsSock.off("disconnect");
    global.zmsSock.off("jobs:dashboard:update");
    if (this._filterDebounce) {
      clearTimeout(this._filterDebounce);
      this._filterDebounce = null;
    }
    global.zmsSock.emit('jobs:dashboard:unsubscribe');
  },

  computed: {
    ...mapGetters([
      'appConfig'
    ]),

    // Dynamic filter options derived from live stats and recent activity
    userOptions() {
      const fromActivity = (this.recentActivity || [])
        .map(a => a && a.userId)
        .filter(v => v && typeof v === 'string');
      // Try to include live user aggregates if available
      const liveArray = (this.stats?.live?.countByUser || this.stats?.live?.countByUserId || []);
      let fromLive = [];
      if (Array.isArray(liveArray)) {
        fromLive = liveArray.map(it => it && (it.name || it.userId)).filter(Boolean);
      } else if (liveArray && typeof liveArray === 'object') {
        // If backend sends an object map { userId: count }
        fromLive = Object.keys(liveArray);
      }
      const merged = [...fromActivity, ...fromLive];
      return Array.from(new Set(merged)).sort((a, b) => a.localeCompare(b));
    },
    jobDefinitionOptions() {
      const fromMetrics = (this.stats?.metrics?.countByDefinition || [])
        .map(it => it && it.name)
        .filter(Boolean);
      const fromActivity = (this.recentActivity || [])
        .map(a => a && a.definition)
        .filter(Boolean);
      const fromLive = (this.stats?.live?.countByDefinition || [])
        .map(it => it && it.name)
        .filter(Boolean);
      const merged = [...fromMetrics, ...fromActivity, ...fromLive];
      return Array.from(new Set(merged)).sort((a, b) => a.localeCompare(b));
    },
    queueOptions() {
      const fromMetrics = (this.stats?.metrics?.countByQueue || [])
        .map(it => it && it.name)
        .filter(Boolean);
      const fromActivity = (this.recentActivity || [])
        .map(a => a && a.queue)
        .filter(Boolean);
      // Live queues can be an array of {name,...} or an object map
      const liveQueues = this.stats?.live?.countByQueue || [];
      let fromLive = [];
      if (Array.isArray(liveQueues)) {
        fromLive = liveQueues.map(it => it && it.name).filter(Boolean);
      } else if (liveQueues && typeof liveQueues === 'object') {
        fromLive = Object.keys(liveQueues);
      }
      const merged = [...fromMetrics, ...fromActivity, ...fromLive];
      return Array.from(new Set(merged)).sort((a, b) => a.localeCompare(b));
    },
    statusOptions() {
      const fromMetrics = (this.stats?.metrics?.countByStatus || [])
        .map(it => it && it.name)
        .filter(Boolean);
      const fromActivity = (this.recentActivity || [])
        .map(a => a && a.status)
        .filter(Boolean);
      const fromLive = (this.stats?.live?.countByStatus || [])
        .map(it => it && it.name)
        .filter(Boolean);
      const merged = Array.from(new Set([...fromMetrics, ...fromActivity, ...fromLive]))
        .sort((a, b) => a.localeCompare(b));
      // Return as objects for v-select item-text/item-value
      return merged.map(name => ({ text: name, value: name }));
    },
    typeOptions() {
      // Derive from recentActivity type if present; otherwise empty and the select will be hidden
      const types = (this.recentActivity || [])
        .map(a => a && a.type)
        .filter(Boolean);
      const unique = Array.from(new Set(types)).sort((a, b) => a.localeCompare(b));
      // If values match known keys, you can translate their labels here; otherwise use raw
      return unique.map(v => ({ text: v, value: v }));
    },
    hasAnyFilterOptions() {
      return (
        (this.userOptions && this.userOptions.length > 0) ||
        (this.jobDefinitionOptions && this.jobDefinitionOptions.length > 0) ||
        (this.queueOptions && this.queueOptions.length > 0) ||
        (this.statusOptions && this.statusOptions.length > 0) ||
        (this.typeOptions && this.typeOptions.length > 0)
      );
    },

    // Live statistics
    liveStats() {

      // countByStatus: [{name: 'RUNNING', count: 10}, {name: 'PENDING', count: 5}]

      let countByStatus = {}
      for (let s of this.stats?.live?.countByStatus ?? []) {
        countByStatus[s.name] = s.count;
      }

      return {
        running: countByStatus?.RUNNING || 0,
        pending: countByStatus?.PENDING || 0,
        total: this.stats?.live?.total || 0
      };
    },

    liveStatusData() {

      let countByStatus = { SUCCESS: 0, FAILED: 0, RUNNING: 0, CANCELLED: 0 }
      for (let s of this.stats?.live?.countByStatus ?? []) {
        countByStatus[s.name] = s.count;
      }

      return countByStatus;
    },

    liveQueueData() {
      const data = this.stats?.live?.countByQueue || {};
      return Object.entries(data)
        .map(([name, count]) => ({ name, count, running: count, pending: 0 }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
    },

    liveQueueStackedData() {
      // Use byQueueStatus if available, otherwise simulate with countByQueue
      const byQueueStatus = this.stats?.live?.countByQueue || [];
      if (byQueueStatus.length > 0) {
        return byQueueStatus.map(item => ({
          name: item.name,
          running: item.statues?.RUNNING || 0,
          pending: item.statues?.PENDING || 0,
          total: item.total || (item.statues?.RUNNING || 0) + (item.statues?.PENDING || 0)
        }));
      }

      // Fallback: use countByQueue and simulate running/pending split
      const data = this.stats?.live?.countByQueue || {};
      return Object.entries(data).map(([name, total]) => ({
        name,
        running: Math.floor(total * 0.7), // Simulate 70% running
        pending: Math.floor(total * 0.3), // Simulate 30% pending
        total
      }));
    },

    liveDefinitionStackedData() {
      // Use byDefinitionStatus if available, otherwise simulate with countByDefinition
      const byDefinitionStatus = this.stats?.live?.countByDefinition || [];
      if (byDefinitionStatus.length > 0) {
        return byDefinitionStatus.map(item => ({
          name: item.name,
          running: item.statues?.RUNNING || 0,
          pending: item.statues?.PENDING || 0,
          total: item.total || (item.statues?.RUNNING || 0) + (item.statues?.PENDING || 0)
        }));
      }

      // Fallback: no detail available
      return [];
    },

    // Historical statistics
    hasHistoricalData() {
      return !!this.stats?.metrics;
    },

    historicalStats() {
      const metrics = this.stats?.metrics || {};
      return {
        tasks: metrics.countLastHour?.count ?? metrics.tasks ?? 0,
        failures: metrics.failuresLastHour?.count ?? metrics.failures ?? 0,
        successRate: +(metrics.successRate?.rate ?? metrics.successRate ?? 0),
        maxWait: +(metrics.maxWaitTime?.maxWait ?? metrics.maxWait ?? 0),
        avgDuration: +(metrics.avgExecutionTime?.avgDur ?? metrics.avgDuration ?? 0),
        tps: +(metrics.tpsLastMinute?.tps ?? metrics.tps ?? 0)
      };
    },

    tpsSparklineData() {
      // Mock sparkline data for TPS
      return [2.1, 2.3, 1.8, 2.5, 2.2, 2.4, 2.0, 2.6, 2.3];
    },

    definitionDistribution() {
      const data = this.stats?.metrics?.countByDefinition || [];
      return this.transformDistributionData(data);
    },

    queueDistribution() {
      const data = this.stats?.metrics?.countByQueue || [];
      return this.transformDistributionData(data);
    },

    statusDistribution() {
      const data = this.stats?.metrics?.countByStatus || [];
      return this.transformDistributionData(data);
    },

    recentActivity() {
      return this.stats?.recentActivity || [];
    },

    // Recent activity
    hasRecentActivity() {
      return this.recentActivity && this.recentActivity.length > 0;
    },


    filteredRecentActivity() {
      let filtered = [...this.recentActivity];

      if (this.activitySearch) {
        const search = this.activitySearch.toLowerCase();
        filtered = filtered.filter(item =>
          (item.definition && item.definition.toLowerCase().includes(search)) ||
          (item.queue && item.queue.toLowerCase().includes(search)) ||
          (item.userId && item.userId.toLowerCase().includes(search)) ||
          (item.place && item.place.toLowerCase().includes(search))
        );
      }

      // Apply global filters
      if (this.filters.user) {
        filtered = filtered.filter(item => item.userId === this.filters.user);
      }
      if (this.filters.jobDefinition) {
        filtered = filtered.filter(item => item.definition.includes(this.filters.jobDefinition));
      }
      if (this.filters.queue) {
        filtered = filtered.filter(item => item.queue === this.filters.queue);
      }
      if (this.filters.status) {
        filtered = filtered.filter(item => item.status === this.filters.status);
      }

      return filtered;
    },

    // Infrastructure data
    queuesHealth() {
      const q = this.stats?.queues;

      // Build worker counts by queue from live workers if available
      const w = this.stats?.workers;
      let workersArr = [];
      if (w && typeof w === 'object') {
        workersArr = Array.isArray(w) ? w : Object.entries(w).map(([id, obj]) => ({ id, ...(obj || {}) }));
      }
      const byQueue = {};
      for (const wi of workersArr) {
        const queue = wi.queue || wi.currentQueue || '';
        if (!queue) continue;
        const status = (wi.status || wi.state || '').toString().toLowerCase();
        if (!byQueue[queue]) byQueue[queue] = { total: 0, busy: 0 };
        byQueue[queue].total += 1;
        if (status === 'busy' || status === 'working' || status === 'running') {
          byQueue[queue].busy += 1;
        }
      }

      if (q && typeof q === 'object') {
        // map object or array into normalized list
        const list = Array.isArray(q)
          ? q
          : Object.entries(q).map(([name, obj]) => ({ name, ...(obj || {}) }));
        return list.map(item => {
          const qName = item.name;
          const derived = byQueue[qName] || { total: 0, busy: 0 };
          const totalWorkers = +(item.totalWorkers ?? item.total ?? 0) || derived.total;
          const busyWorkers = +(item.busyWorkers ?? item.busy ?? 0) || derived.busy;
          const idleWorkers = +(item.idleWorkers ?? item.idle ?? (totalWorkers - busyWorkers));
          return {
            name: qName,
            totalWorkers: Math.max(0, totalWorkers),
            busyWorkers: Math.max(0, Math.min(busyWorkers, totalWorkers)),
            idleWorkers: Math.max(0, isNaN(idleWorkers) ? (Math.max(0, totalWorkers - busyWorkers)) : idleWorkers),
            inflightJobs: +(item.inflightJobs ?? item.inflight ?? item.running ?? 0),
            pendingJobs: +(item.pendingJobs ?? item.backlog ?? item.pending ?? 0),
            avgWaitTime: +(item.avgWaitTime ?? item.avgWait ?? 0),
            lastActivity: item.lastActivity || item.updatedAt || item.ts || null
          };
        });
      }
      // Fallback to previous mock
      return [];
    },

    workersList() {
      const w = this.stats?.workers;
      if (w && typeof w === 'object') {
        const list = Array.isArray(w)
          ? w
          : Object.entries(w).map(([id, obj]) => ({ id, ...(obj || {}) }));
        return list.map(item => ({
          id: item.id || item.name,
          status: (item.status || item.state || 'idle').toLowerCase(),
          host: item.host || item.hostname || '',
          queue: item.queue || item.currentQueue || '',
          currentJob: item.currentJob || item.jobClassName,
          jobId: item.jobId || item.jobID || item.jid || null,
          executionId: item.executionId || item.executionID || item.execId || item.execution_id || null,
          lastSeen: item.lastSeen || item.updatedAt || item.ts || null,
          startedAt: item.startedAt || item.jobStartedAt || null,
          createdAt: item.createdAt || item.created_at || item.workerCreatedAt || null
        }));
      }
      // Fallback mock
      return [];
    },

    machinesData() {
      const m = this.stats?.machines;
      if (m && typeof m === 'object') {
        const list = Array.isArray(m)
          ? m
          : Object.entries(m).map(([host, obj]) => ({ host, ...(obj || {}) }));
        return list.map(item => ({
          host: item.host || item.hostname || '',
          workers: +(item.workersOnline ?? item.workers ?? 0),
          totalWorkers: +(item.workersTotal ?? item.totalWorkers ?? item.workers ?? 0),
          status: (item.status || item.state || 'online').toLowerCase(),
          cpu: +(item.cpu ?? item.cpuLoad ?? 0),
          memory: +(item.memory ?? item.memLoad ?? 0),
          lastSeen: item.lastSeen || item.updatedAt || item.ts || null
        }));
      }
      // Fallback mock
      return [];
    },

    // Filter helpers
    hasActiveFilters() {
      return Object.values(this.filters).some(value => value !== null && value !== '');
    }
  },

  watch: {
    filters: {
      handler() {
        // Debounce resubscribe to avoid rapid consecutive emits
        if (this._filterDebounce) {
          clearTimeout(this._filterDebounce);
          this._filterDebounce = null;
        }
        this._filterDebounce = setTimeout(() => {
          this.setDashboardFilter(this.filters);
          this._filterDebounce = null;
        }, 150);
      },
      deep: true
    }
  },

  methods: {
    setDashboardFilter(f = {}) {
      // Always clean the previous subscription first
      try { global.zmsSock.emit('jobs:dashboard:unsubscribe'); } catch (e) { /* ignore */ }
      const payload = {
        filters: {
          userId:         (f.user || '').toString().trim(),
          definitionName: (f.jobDefinition || '').toString().trim(),
          jobType:        (f.type || '').toString().trim(),
          queue:          (f.queue || '').toString().trim(),
          status:         ((f.status || '').toString().trim()).toUpperCase(),
        }
      };
      global.zmsSock.emit('jobs:dashboard:subscribe', payload);
    },

    onSocketConnection() {
      // Re-subscribe with current filters on reconnect
      this.setDashboardFilter(this.filters);
    },

    // Filter management
    resetFilters() {
      this.filters = {
        user: null,
        jobDefinition: null,
        type: null,
        queue: null,
        status: null
      };
    },

    clearFilter(filterName) {
      this.filters[filterName] = null;
    },

    // Chart interactions
    onStatusClick(status) {
      this.filters.status = status.name;
    },

    onQueueClick(queue) {
      this.filters.queue = queue.name;
    },

    onDefinitionClick(definition) {
      this.filters.jobDefinition = definition.name;
    },

    onQueueStatusClick(event) {
      // Handle combined queue + status filter from stacked bars
      if (event.queue) {
        this.filters.queue = event.queue;
      }
      if (event.status) {
        this.filters.status = event.status;
      }
    },

    onDefinitionStatusClick(event) {
      // Handle combined definition + status filter from stacked bars
      if (event.definition) {
        this.filters.jobDefinition = event.definition;
      }
      if (event.status) {
        this.filters.status = event.status;
      }
    },


    showExecutionDetails(execution) {
      this.selectedExecution = execution;
      this.showDetailsPanel = true;
    },

    transformDistributionData(data) {
      if (!data) return [];
      return data
        .sort((a, b) => b.count - a.count);
    }
  },
};
</script>

<style lang="scss" scoped>
// Grid system and color palette
.job-manager-dashboard {
  --gutter-size: 24px;
  --card-padding: 16px;
  --card-radius: 12px;

  // Status colors (consistent palette)
  --status-running: #3B82F6;
  --status-pending: #F59E0B;
  --status-success: #10B981;
  --status-failed: #EF4444;
  --status-cancelled: #9CA3AF;

  background-color: #fafafa;
  min-height: 100vh;
}

// Header styling
.dashboard-header {
  background: white;
  border-bottom: 1px solid #e0e0e0;
  padding: 24px 0;

  h1 {
    color: #1a202c;
    font-weight: 600;
    line-height: 1.2;
  }

  p {
    font-size: 0.875rem;
    opacity: 0.7;
  }
}

// Tab navigation
.dashboard-tabs {
  border-bottom: 1px solid #e0e0e0;

  ::v-deep .v-tab {
    font-weight: 500;
    text-transform: none;
    font-size: 0.875rem;
    padding: 0 24px;

    &.v-tab--active {
      color: var(--v-primary-base);
      border-bottom: 2px solid var(--v-primary-base);
    }

    &:not(.v-tab--active) {
      color: #6b7280;

      &:hover {
        color: #374151;
      }
    }
  }
}

// Filter bar styling
.filter-bar {
  gap: 16px;

  .filter-select {
    flex-shrink: 0;

    ::v-deep .v-select__selection {
      font-size: 0.875rem;
    }

    ::v-deep .v-text-field__details {
      display: none;
    }
  }
}

.filter-chips {
  gap: 8px;
}

// Section styling
.dashboard-section {
  margin-bottom: 2.5rem;

  h2 {
    color: #1f2937;
    font-weight: 600;
    margin-bottom: 1rem;
    font-size: 1.125rem;
  }

  // Live section special styling
  &:first-of-type h2 {
    display: flex;
    align-items: center;
    gap: 16px;

    span {
      font-size: 0.75rem;
      color: #6b7280;
      font-weight: 400;
    }
  }
}

// Card styling
::v-deep .v-card {
  border-radius: var(--card-radius);
  padding: var(--card-padding);
  transition: all 0.15s ease-in-out;
  border: 1px solid #e5e7eb;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #d1d5db;
  }
}

// KPI Cards styling
::v-deep .live-kpi-card,
::v-deep .kpi-card {

  .v-card-title {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    padding-bottom: 8px;
  }

  .kpi-value {
    font-size: 2rem;
    font-weight: bold;
    line-height: 1;
    color: #111827;
  }

  .kpi-unit {
    font-size: 0.75rem;
    color: #9ca3af;
    margin-top: 4px;
  }
}

// Status badges
::v-deep .v-chip {
  font-weight: 500;

  &.status-running {
    background-color: var(--status-running) !important;
  }

  &.status-pending {
    background-color: var(--status-pending) !important;
  }

  &.status-success {
    background-color: var(--status-success) !important;
  }

  &.status-failed {
    background-color: var(--status-failed) !important;
  }

  &.status-cancelled {
    background-color: var(--status-cancelled) !important;
  }
}

// Activity table styling
::v-deep .recent-activity-table {
  .v-data-table__wrapper {
    border-radius: var(--card-radius);
  }

  .row-failed {
    background-color: #fef2f2 !important;
  }

  tbody tr:hover {
    background-color: rgba(0, 0, 0, 0.04) !important;
  }
}

// Infrastructure section
::v-deep .v-expansion-panel {
  border-radius: var(--card-radius) !important;
  box-shadow: none;
  border: 1px solid #e5e7eb;

  &::before {
    box-shadow: none;
  }

  .v-expansion-panel-header {
    padding: 16px 24px;
    font-weight: 600;
  }

  .v-expansion-panel-content__wrap {
    padding: 0 24px 24px;
  }
}

// Desktop layout (≥1280px)
@media (min-width: 1280px) {
  .job-manager-dashboard {
    // 12 columns with 24px gutter
    .v-container {
      max-width: 1440px;
      padding-left: 32px;
      padding-right: 32px;
    }

    .v-row {
      margin-left: -12px;
      margin-right: -12px;

      .v-col {
        padding-left: 12px;
        padding-right: 12px;
      }
    }

    // Live KPIs: 3 cards on desktop
    .dashboard-section:nth-of-type(1) {
      .v-col[class*="md-4"] {
        flex: 0 0 33.333333%;
        max-width: 33.333333%;
      }
    }

    // Historical KPIs: 6 cards, 2 cols each
    .dashboard-section:nth-of-type(2) {
      .v-col[class*="md-2"] {
        flex: 0 0 16.666667%;
        max-width: 16.666667%;
      }
    }

    // Historical distributions: 3 cards, 4 cols each
    .dashboard-section:nth-of-type(2) {
      .v-col[class*="md-4"]:not(.v-col[class*="md-2"]) {
        flex: 0 0 33.333333%;
        max-width: 33.333333%;
      }
    }
  }
}

// Tablet layout (768-1279px)
@media (min-width: 768px) and (max-width: 1279px) {
  .job-manager-dashboard {
    .v-container {
      max-width: none;
      padding-left: 24px;
      padding-right: 24px;
    }

    // Adjust to 6 columns on tablet
    .dashboard-section {
      .v-col[class*="md-4"] {
        flex: 0 0 50%;
        max-width: 50%;
      }

      .v-col[class*="md-2"] {
        flex: 0 0 50%;
        max-width: 50%;
      }

      .v-col[class*="md-6"] {
        flex: 0 0 100%;
        max-width: 100%;
        margin-bottom: 16px;
      }
    }

    // Filter bar adjustments
    .filter-bar {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;

      .filter-select {
        min-width: 200px;
      }
    }
  }
}

// Mobile layout (<768px)
@media (max-width: 767px) {
  .job-manager-dashboard {
    .v-container {
      padding-left: 16px;
      padding-right: 16px;
    }

    .dashboard-header {
      padding: 16px 0;

      h1 {
        font-size: 1.5rem;
      }
    }

    // Everything stacks on mobile
    .dashboard-section {
      margin-bottom: 2rem;

      h2 {
        font-size: 1.125rem;
        margin-bottom: 0.75rem;
      }

      .v-col {
        flex: 0 0 100% !important;
        max-width: 100% !important;
        margin-bottom: 1rem;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    // Mobile filter bar
    .filter-bar {
      flex-direction: column;
      align-items: stretch;
      gap: 12px;

      .filter-select {
        width: 100%;
        min-width: auto;
      }

      .v-btn {
        align-self: flex-start;
      }
    }

    // Mobile KPI cards
    ::v-deep .live-kpi-card,
    ::v-deep .kpi-card {
      height: 80px;

      .kpi-value {
        font-size: 1.5rem;
      }
    }

    // Mobile activity table
    ::v-deep .recent-activity-table {
      .v-data-table__wrapper {
        overflow-x: auto;
        min-width: 600px;
      }
    }
  }
}

// Loading and empty states
.dashboard-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;

  .v-progress-circular {
    margin-bottom: 16px;
  }
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;

  .v-icon {
    margin-bottom: 16px;
    opacity: 0.5;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
  }
}

// Transitions and animations
.dashboard-section,
.v-card,
.v-chip,
.v-btn {
  transition: all 0.15s ease-in-out;
}

// Skeleton loading states
.skeleton-loading {
  .v-skeleton-loader {
    border-radius: var(--card-radius);
  }
}

// Chart containers
::v-deep .chart-container {
  height: 240px;

  .v-card-title {
    padding-bottom: 8px;
    border-bottom: 1px solid #f3f4f6;
  }
}

// Ensure proper spacing and no sidebar overlap
.job-manager-dashboard {
  margin-left: 0;
  padding-left: 0;
}

/* Equal-height infrastructure cards and two-column infra layout */
.infrastructure-section {
  .infra-grid {
    align-items: stretch;
  }
  .infra-left,
  .infra-right {
    display: flex;
  }
  .infra-left {
    flex-direction: column;
    gap: 12px;
    min-height: 0; /* allow children to size */
  }

}
</style>
